import React from 'react';
import { View, StyleSheet } from 'react-native';
import Svg, { Path, Rect, G } from 'react-native-svg';

export type CalendarProviderId = 'google' | 'outlook' | 'apple';

interface CalendarProviderBrandIconProps {
  provider: CalendarProviderId;
  /** Outer box size. Default 28. */
  size?: number;
}

/**
 * Standard-style provider marks for calendar integration rows (SVG, no emoji).
 * Google: official four-color “G” geometry (48×48 viewBox). Outlook: Microsoft blue tile + envelope.
 * Apple: bitten-apple silhouette consistent with other in-app Apple marks.
 */
const CalendarProviderBrandIcon: React.FC<CalendarProviderBrandIconProps> = ({ provider, size = 28 }) => {
  const s = size;
  const label =
    provider === 'google' ? 'Google Calendar' : provider === 'outlook' ? 'Microsoft Outlook' : 'Apple Calendar';

  switch (provider) {
    case 'google':
      return (
        <View style={[styles.box, { width: s, height: s }]} accessibilityLabel={label}>
          <Svg width={s} height={s} viewBox="0 0 48 48">
            <Path
              fill="#EA4335"
              d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.21 17.74 9.5 24 9.5z"
            />
            <Path
              fill="#4285F4"
              d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.95-2.26 5.48-4.78 7.18l7.73 6C43.98 37.08 46.98 31.38 46.98 24.55z"
            />
            <Path
              fill="#FBBC05"
              d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
            />
            <Path
              fill="#34A853"
              d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-3.71-13.47-8.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
            />
          </Svg>
        </View>
      );

    case 'outlook':
      return (
        <View style={[styles.box, { width: s, height: s }]} accessibilityLabel={label}>
          <Svg width={s} height={s} viewBox="0 0 24 24">
            <Rect width="24" height="24" rx="4.5" fill="#0078D4" />
            <Path
              fill="#FFFFFF"
              d="M5.5 7.5h8.2c0.55 0 1 0.45 1 1v7c0 0.55-0.45 1-1 1H5.5c-0.55 0-1-0.45-1-1v-7c0-0.55 0.45-1 1-1z"
            />
            <Path fill="#0078D4" d="M14.2 9.2H6.8v5.6h7.4V9.2z" />
            <Path fill="#FFFFFF" d="M6.8 9.2l3.7 2.8 3.7-2.8" opacity={0.95} />
            <Path fill="#FFFFFF" d="M15.2 7.5h4.3l-4.3 2.15V7.5z" />
          </Svg>
        </View>
      );

    case 'apple':
      return (
        <View style={[styles.box, styles.appleTile, { width: s, height: s }]} accessibilityLabel={label}>
          <Svg width={s} height={s} viewBox="0 0 24 24">
            <G transform="translate(12,12.1) scale(0.72) translate(-12,-12)">
              <Path
                fill="#000000"
                d="M16.5 10.2c-.02 1.98-1.08 3.48-2.65 3.48-1.25 0-1.76-.67-3.28-.67-1.5 0-1.98.67-3.23.67-1.58 0-2.72-1.65-2.72-3.8 0-2.45 1.35-4.35 3.35-4.35 1.05 0 1.83.45 2.45.45.6 0 1.73-.48 2.92-.48.47 0 2.16.04 3.26 1.58-2.6 1.43-2.18 4.2.9 5.12zm-4.1-7.7c.52-.62 1.35-1.05 2.15-1.05.1 1.05-.35 2.05-.95 2.78-.55.65-1.45 1.15-2.33 1.08-.12-1.02.4-2.05 1.13-2.81z"
              />
            </G>
          </Svg>
        </View>
      );

    default:
      return null;
  }
};

const styles = StyleSheet.create({
  box: {
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  appleTile: {
    backgroundColor: '#F2F2F7',
    borderRadius: 6,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#E5E5EA',
  },
});

export default CalendarProviderBrandIcon;
